import React from 'react'
import validator from 'validator'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { firebaseConnect } from 'react-redux-firebase'
import { reduxForm, Field, SubmissionError } from 'redux-form'
import { routerActions } from 'react-router-redux'
import { FormattedMessage, defineMessages, intlShape } from 'react-intl'
import { isValidAddress, isValidChecksumAddress } from 'ethereumjs-util'
import { sleep } from '../helpers'

import * as UI from '../components'
import * as commands from '../commands'
import * as constants from '../constants'

const walletOptions = constants.walletOptions

const messages = defineMessages({
  haveAnAccount: {
    id: 'sign-up.have-an-account',
    defaultMessage: 'Already have an account?',
  },
  signIn: {
    id: 'sign-up.sign-in-message',
    defaultMessage: 'Click here to sign in!',
  },
  header: {
    id: 'sign-up.header',
    defaultMessage:
      'Registering will speed up your purchase process on the sale day. Do it.',
  },
  emailLabel: { id: 'sign-up.email.label', defaultMessage: 'E-mail Address' },
  emailPlaceholder: {
    id: 'sign-up.email.placeholder',
    defaultMessage: 'Your e-mail address',
  },
  passwordLabel: { id: 'sign-up.password.label', defaultMessage: 'Password' },
  passwordPlaceholder: {
    id: 'sign-up.password.placeholder',
    defaultMessage: 'Make it a good one',
  },
  returnAddressHint: {
    id: 'sign-up.returnAddress.hint',
    defaultMessage: 'Optional, you can provide or update this later.',
  },
  returnAddressLabel: {
    id: 'sign-up.returnAddress.label',
    defaultMessage: 'Ethereum Return Address',
  },
  returnAddressPlaceholder: {
    id: 'sign-up.returnAddress.placeholder',
    defaultMessage: 'Your address',
  },
  walletLabel: { id: 'sign-up.wallet.label', defaultMessage: 'Wallet' },
  walletPlaceholder: {
    id: 'sign-up.wallet.placeholder',
    defaultMessage: 'Select one...',
  },
  walletHint: {
    id: 'sign-up.wallet.hint',
    defaultMessage:
      'Incompatible: Exodus, Coinbase. Poloniex, Kraken, or any exchange.',
  },
  referralCode: {
    id: 'log-in.referral-code',
    defaultMessage: 'Invitation Code',
  },
  referralCodePlaceholder: {
    id: 'sign-up.referral-code.placeholder',
    defaultMessage: 'Optional',
  },
  submit: { id: 'sign-up.submit', defaultMessage: 'Register' },
  termsAndConditions: {
    id: 'sign-up.terms-and-conditions',
    defaultMessage:
      'By clicking register, you agree to our Terms and Conditions',
  },
})

class SignUp extends React.Component {
  componentWillMount() {
    const { firebase, dispatch, redeemReferralCode } = this.props
    firebase.auth().onAuthStateChanged(function(user) {
      if (user) {
        redeemReferralCode()
        dispatch(routerActions.replace('/'))
      }
    })
  }

  render() {
    const {
      createUser,
      error,
      handleSubmit,
      invalid,
      pristine,
      storefront,
      submitFailed,
      submitting,
      trackTAC,
    } = this.props

    const { intl } = this.context
    const registrationHeader = {
      id: 'storefront.header',
      defaultMessage: `Register for ${storefront.name} now!`,
    }
    return (
      <UI.Form onSubmit={handleSubmit(createUser)}>
        <UI.Card image={storefront.iconUrl}>
          <UI.Divider hidden />

          <UI.Text center size="huge" weight="bold" color="primary">
            <FormattedMessage {...registrationHeader} />
          </UI.Text>

          <UI.Divider hidden />

          {submitFailed && <UI.Message type="error">{error}</UI.Message>}

          <UI.Divider hidden />

          <Field
            fluid
            icon="user"
            iconPosition="left"
            label={intl.formatMessage(messages.emailLabel)}
            placeholder={intl.formatMessage(messages.emailPlaceholder)}
            name="email"
            component={UI.TextField}
          />

          <Field
            fluid
            icon="lock"
            iconPosition="left"
            label={intl.formatMessage(messages.passwordLabel)}
            placeholder={intl.formatMessage(messages.passwordPlaceholder)}
            name="password"
            type="password"
            component={UI.TextField}
          />

          <Field
            fluid
            label={intl.formatMessage(messages.returnAddressLabel)}
            placeholder={intl.formatMessage(messages.returnAddressPlaceholder)}
            name="destinationAddress"
            component={UI.TextField}
            hint={intl.formatMessage(messages.returnAddressHint)}
          />

          <Field
            fluid
            label={intl.formatMessage(messages.walletLabel)}
            placeholder={intl.formatMessage(messages.walletPlaceholder)}
            name="wallet"
            component={UI.SelectField}
            options={walletOptions}
            hint={intl.formatMessage(messages.walletHint)}
          />

          <UI.Button disabled={pristine || invalid || submitting}>
            <FormattedMessage {...messages.submit} />
          </UI.Button>

          <UI.Divider hidden />

          <UI.Text size="small" center>
            <FormattedMessage {...messages.termsAndConditions} />
            <UI.Link
              to="https://coincart.co/terms"
              target="_blank"
              onClick={trackTAC}
            >
              Terms and Conditions
            </UI.Link>
          </UI.Text>
        </UI.Card>

        <UI.Text center size="small">
          <FormattedMessage {...messages.haveAnAccount} />

          <UI.Link to="/login">
            &nbsp; <FormattedMessage {...messages.signIn} />
          </UI.Link>
        </UI.Text>

        <UI.PoweredBy />
      </UI.Form>
    )
  }
}

SignUp.contextTypes = {
  intl: intlShape,
}

const selectors = createStructuredSelector({
  initialValues: () => ({
    destinationAddress: '',
    email: '',
    password: '',
    wallet: '',
  }),
  storefrontId: ({ firebase }) => process.env.REACT_APP_STOREFRONT_ID,
})

const actions = (dispatch, props) => ({
  trackTAC: async () => {
    dispatch({ type: 'TAC_VISITED', track: {} })
  },

  redeemReferralCode: async () => {
    try {
      const authToken = await props.firebase.auth().currentUser.getIdToken(true)
      const referralCode = props.location.query.ref

      if (referralCode) {
        await commands.redeemCode(authToken, { referralCode })
      }
    } catch (error) {
      throw new SubmissionError({ _error: error.message })
    }
  },

  createUser: async ({ email, password, ...profile }) => {
    let isUserCreated = false
    try {
      const storefrontId = props.storefrontId
      const signUpData = {
        email,
        password,
        storefrontId,
        referralCode: props.location.query.ref,
        displayName: profile.displayName,
        destinationAddress: profile.destinationAddress,
        wallet: profile.wallet,
      }

      // Create account
      await commands.signUp(signUpData)
      isUserCreated = true
      dispatch({
        type: 'REGISTRATION',
        track: { signUpData },
      })

      // Wait to avoid occasional race condition
      await sleep(500)

      // Log in
      await props.firebase.login({ email, password })
      dispatch(routerActions.push('/'))
    } catch (error) {
      // If there is an error logging in, assume it is due to a duplicate email
      // This may need to be improved in the future
      const _error = isUserCreated
        ? 'Email address is already registered'
        : error.message

      throw new SubmissionError({ _error })
    }
  },
})

const validate = values => {
  const errors = {}

  if (validator.isEmpty(values.email))
    errors.email = 'E-mail address is required'
  if (!validator.isEmail(values.email))
    errors.email = 'Please enter a valid email address'
  if (
    !validator.isEmpty(values.destinationAddress) &&
    !isValidAddress(values.destinationAddress)
  )
    errors.destinationAddress = 'This is an invalid Ethereum Address'
  if (validator.isEmpty(values.password))
    errors.password = 'Password is required'
  if (!validator.isLength(values.password, { min: 8, max: undefined }))
    errors.password = 'Password must be at least 8 characters'

  return errors
}

const warn = values => {
  const warn = {}

  if (
    !validator.isEmpty(values.destinationAddress) &&
    isValidAddress(values.destinationAddress) &&
    !isValidChecksumAddress(values.destinationAddress)
  )
    warn.destinationAddress =
      'This is an invalid (EIP-55) Ethereum Address - please double-check'

  return warn
}

export default compose(
  firebaseConnect(),
  connect(selectors, actions),
  reduxForm({ form: 'SignUp', validate, warn })
)(SignUp)



// WEBPACK FOOTER //
// ./src/screens/SignUp.js